import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api/client";
import { i18n } from "../i18n";
import "./app-dialog";

const localDay = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/** Calendar boundaries deliberately use local dates, including DST transitions. */
export function exportDateRange(from: string, to: string) {
  const parse = (value: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error("invalid_date");
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime()) || localDay(date) !== value)
      throw new Error("invalid_date");
    return date;
  };
  const start = parse(from);
  const end = parse(to);
  if (start > end) throw new Error("invalid_date_range");
  end.setDate(end.getDate() + 1);
  return { started_from: start.toISOString(), started_to: end.toISOString() };
}

@customElement("run-history-export")
export class RunHistoryExport extends LitElement {
  @property() kind: "codex" | "tasks" = "codex";
  @property({ attribute: false }) filters: Record<
    string,
    string | number | undefined
  > = {};
  @state() private open = false;
  @state() private busy = false;
  @state() private from = "";
  @state() private to = "";
  @state() private error = "";

  protected createRenderRoot() {
    return this;
  }

  private show() {
    const today = new Date();
    this.to = localDay(today);
    today.setDate(today.getDate() - 6);
    this.from = localDay(today);
    this.error = "";
    this.open = true;
  }

  private close() {
    if (!this.busy) this.open = false;
  }

  private saveFile(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private async download() {
    if (this.busy) return;
    this.error = "";
    let range;
    try {
      range = exportDateRange(this.from, this.to);
    } catch {
      this.error = i18n.t("historyExportInvalidDate");
      return;
    }
    this.busy = true;
    try {
      const filters = { ...this.filters };
      // The download is independent of the currently displayed page.
      for (const key of ["page", "page_size", "limit", "offset"])
        delete filters[key];
      for (const key of ["from", "to"]) {
        if (filters[key])
          filters[key] = new Date(String(filters[key])).toISOString();
      }
      const blob = await api.exportRunHistory(this.kind, {
        ...filters,
        ...range,
      });
      this.saveFile(blob, `${this.kind}-runs-${this.from}-${this.to}.xlsx`);
      this.open = false;
    } catch (error) {
      this.error = (error as Error).message;
    } finally {
      this.busy = false;
    }
  }

  render() {
    return html`<div class="history-export-actions">
        <a2k-button type="button" ?disabled=${this.busy} @click=${this.show}>
          ${i18n.t("historyExportExcel")}
        </a2k-button>
      </div>
      <app-dialog
        .open=${this.open}
        .busy=${this.busy}
        heading=${i18n.t("historyExportExcel")}
        confirm-label=${i18n.t("historyExportExcel")}
        cancel-label=${i18n.t("cancel")}
        working-label=${i18n.t("historyExportBusy")}
        @dialog-cancel=${this.close}
        @dialog-confirm=${this.download}
      >
        <p>${i18n.t("historyExportHelp")}</p>
        <div class="form-grid">
          <label class="native-field"
            ><span>${i18n.t("historyExportFrom")}</span>
            <input
              class="a2k-native-input"
              type="date"
              .value=${this.from}
              ?disabled=${this.busy}
              @input=${(event: Event) => {
                this.from = (event.target as HTMLInputElement).value;
              }}
            />
          </label>
          <label class="native-field"
            ><span>${i18n.t("historyExportTo")}</span>
            <input
              class="a2k-native-input"
              type="date"
              .value=${this.to}
              ?disabled=${this.busy}
              @input=${(event: Event) => {
                this.to = (event.target as HTMLInputElement).value;
              }}
            />
          </label>
        </div>
        ${this.error ? html`<p class="status status-error" role="alert">${this.error}</p>` : null}
      </app-dialog>`;
  }
}
